import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";
import { Menu, X, Download } from "lucide-react";

const NAV = [
    { label: "Fonctionnement", to: "/#how", id: "nav-how" },
    { label: "Sécurité", to: "/#security", id: "nav-security" },
    { label: "FAQ", to: "/#faq", id: "nav-faq" },
    { label: "Contact", to: "/contact", id: "nav-contact" },
];

export default function Header() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const { pathname } = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [pathname]);

    return (
        <header
            data-testid="site-header"
            className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
                scrolled
                    ? "bg-white/85 backdrop-blur-2xl border-b border-slate-200"
                    : "bg-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-8 h-20 flex items-center justify-between">
                <Link
                    to="/"
                    data-testid="brand-logo"
                    className="group inline-flex items-center"
                >
                    <img
                        src="/brand/coway-logo.png"
                        srcSet="/brand/coway-logo-small.png 1x, /brand/coway-logo@2x.png 2x"
                        alt="Coway"
                        width="128"
                        height="32"
                        className="h-7 sm:h-8 w-auto select-none"
                        draggable={false}
                    />
                </Link>

                <nav className="hidden md:flex items-center gap-10">
                    {NAV.map((item) =>
                        item.to.startsWith("/#") ? (
                            <a
                                key={item.id}
                                href={item.to}
                                data-testid={item.id}
                                className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
                            >
                                {item.label}
                            </a>
                        ) : (
                            <NavLink
                                key={item.id}
                                to={item.to}
                                data-testid={item.id}
                                className={({ isActive }) =>
                                    `text-sm font-medium transition-colors ${
                                        isActive
                                            ? "text-slate-900"
                                            : "text-slate-600 hover:text-slate-900"
                                    }`
                                }
                            >
                                {item.label}
                            </NavLink>
                        ),
                    )}
                </nav>

                <div className="hidden md:flex items-center gap-3">
                    <a
                        href="/#download"
                        data-testid="header-cta-download"
                        className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-coway text-white font-semibold text-sm hover:bg-coway-hover transition-all hover:scale-[1.03] active:scale-[0.98] shadow-[0_8px_24px_-8px_rgba(81,199,173,0.55)]"
                    >
                        <Download className="w-4 h-4" strokeWidth={2} />
                        Télécharger
                    </a>
                </div>

                <button
                    aria-label="Menu"
                    data-testid="mobile-menu-toggle"
                    onClick={() => setOpen((v) => !v)}
                    className="md:hidden inline-flex items-center justify-center w-11 h-11 rounded-full bg-slate-50 border border-slate-200 text-slate-900"
                >
                    {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                </button>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.25 }}
                        className="md:hidden border-t border-slate-200 bg-white/95 backdrop-blur-2xl"
                        data-testid="mobile-menu"
                    >
                        <div className="px-6 py-6 flex flex-col gap-5">
                            {NAV.map((item) =>
                                item.to.startsWith("/#") ? (
                                    <a
                                        key={item.id}
                                        href={item.to}
                                        data-testid={`m-${item.id}`}
                                        className="text-lg font-medium text-slate-700"
                                    >
                                        {item.label}
                                    </a>
                                ) : (
                                    <Link
                                        key={item.id}
                                        to={item.to}
                                        data-testid={`m-${item.id}`}
                                        className="text-lg font-medium text-slate-700"
                                    >
                                        {item.label}
                                    </Link>
                                ),
                            )}
                            <a
                                href="/#download"
                                data-testid="mobile-cta-download"
                                className="mt-2 inline-flex items-center justify-center gap-2 h-12 px-6 rounded-full bg-coway text-white font-semibold shadow-[0_8px_24px_-8px_rgba(81,199,173,0.55)]"
                            >
                                <Download className="w-4 h-4" />
                                Télécharger l'app
                            </a>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
}

function LogoMark() {
    return null;
}
